import { useTheme } from '@mui/material';
import {
  DateRangePickerDay,
  type DateRangePickerDayProps,
} from '@mui/x-date-pickers-pro/DateRangePickerDay';
import type { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';

export const EventMarkerDay = (
  props: DateRangePickerDayProps<DateTime> & {
    dataDayKeys?: Set<string>;
    eventNamesByDay?: Map<string, string[]>;
  },
) => {
  const { dataDayKeys, eventNamesByDay, ...dayProps } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const dayKey = dayProps.day.toISODate() ?? '';
  const isEventDay = !dayProps.outsideCurrentMonth && eventNamesByDay?.has(dayKey);
  const isNoDataDay = isEventDay && !dataDayKeys?.has(dayKey);

  const dotColor = dayProps.selected
    ? theme.palette.common.white
    : isNoDataDay
    ? theme.palette.error.main
    : theme.palette.success.main;

  const day = (
    <DateRangePickerDay
      {...dayProps}
      sx={{
        ...dayProps.sx,
        position: 'relative',
        '&::after': isEventDay
          ? {
              content: '""',
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 5,
              height: 5,
              borderRadius: '50%',
              // A hollow ring, not just a red dot, marks a day without data (WCAG 1.4.1).
              ...(isNoDataDay
                ? { border: `1.5px solid ${dotColor}`, boxSizing: 'border-box' }
                : { backgroundColor: dotColor }),
            }
          : undefined,
      }}
    />
  );

  const eventNames = isEventDay ? eventNamesByDay?.get(dayKey) : undefined;
  if (!eventNames?.length) return day;

  return (
    <Tooltip
      placement="top"
      title={
        <>
          {eventNames.map((name, i) => (
            // Names can repeat across a day, so the index disambiguates the key.
            <div key={`${name}-${i}`}>{name}</div>
          ))}
          {isNoDataDay && <div>{t('pages.playout_reports.date_range.legend.no_data')}</div>}
        </>
      }
    >
      {day}
    </Tooltip>
  );
};
